import { ReactNode, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { isUserTyping } from "@/lib/utils";

export const SheetNavigation = ({
  betweenButtons,
  canMoveToNext,
  canMoveToPrevious,
  handleMoveToNext,
  handleMoveToPrevious,
  screenName,
  variant = "secondary",
}: {
  betweenButtons?: ReactNode;
  canMoveToNext: boolean;
  canMoveToPrevious: boolean;
  handleMoveToNext: () => void;
  handleMoveToPrevious: () => void;
  screenName: MIXPANEL_SCREENS;
  variant?: "ghost" | "outline" | "secondary";
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // defaultPrevented = some focused UI (e.g. a modal) already consumed
      // the key; navigating candidacies underneath it would be a surprise.
      if (
        isUserTyping() ||
        event.defaultPrevented ||
        event.ctrlKey ||
        event.altKey
      ) {
        return;
      }

      if (!event.metaKey && !event.shiftKey && event.key === "ArrowLeft") {
        if (!canMoveToPrevious) return;
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed("Left Arrow", screenName);
        handleMoveToPrevious();
        return;
      }
      if (!event.metaKey && !event.shiftKey && event.key === "ArrowRight") {
        if (!canMoveToNext) return;
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed("Right Arrow", screenName);
        handleMoveToNext();
        return;
      }

      if (event.metaKey && event.shiftKey && event.key === ",") {
        if (!canMoveToPrevious) return;
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+< (Previous)",
          screenName,
        );
        handleMoveToPrevious();
      } else if (event.metaKey && event.shiftKey && event.key === ".") {
        if (!canMoveToNext) return;
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Shift+> (Next)",
          screenName,
        );
        handleMoveToNext();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    canMoveToNext,
    canMoveToPrevious,
    handleMoveToNext,
    handleMoveToPrevious,
    screenName,
  ]);

  const isGhost = variant === "ghost";
  const isOutline = variant === "outline";
  const isIconButton = isGhost || isOutline;
  const buttonClassName = isGhost
    ? "h-8 w-8 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
    : isOutline
      ? "h-8 w-8"
      : "rounded-full";
  const iconClassName = isGhost ? "h-4 w-4" : "h-4 w-4 text-slate-700";
  const rowClassName = isGhost
    ? "items-center gap-0.5"
    : isOutline
      ? "items-center gap-1.5"
      : "items-center gap-1";

  return (
    <Row className={rowClassName}>
      <Button
        className={buttonClassName}
        disabled={!canMoveToPrevious}
        onClick={() => {
          mixpanelUtils.trackButtonClick("Previous result", screenName);
          handleMoveToPrevious();
        }}
        size={isIconButton ? "icon" : undefined}
        variant={variant}
      >
        <ChevronLeftIcon className={iconClassName} />
      </Button>
      {betweenButtons}
      <Button
        className={buttonClassName}
        disabled={!canMoveToNext}
        onClick={() => {
          mixpanelUtils.trackButtonClick("Next result", screenName);
          handleMoveToNext();
        }}
        size={isIconButton ? "icon" : undefined}
        variant={variant}
      >
        <ChevronRightIcon className={iconClassName} />
      </Button>
    </Row>
  );
};
